import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AddRestaurant from "../restaurant/AddRestaurant";
import {
  extractRestaurantId,
  extractRestaurantName,
  fetchRestaurantById,
} from "../restaurant/restaurantApi";
import EatsAdminLayout from "./EatsAdminLayout";
import EatsAdminCreateStepper from "./EatsAdminCreateStepper";
import EatsAdminPageHeader from "./EatsAdminPageHeader";
import RootAdminAssignPanel from "./RootAdminAssignPanel";
import "./EatsAdminCreatePage.css";

function toActiveRestaurant(restaurant, fallbackId = null) {
  const restaurantId = extractRestaurantId(restaurant) || fallbackId;
  if (!restaurantId) return null;
  return {
    restaurantId,
    restaurantName: extractRestaurantName(restaurant) || restaurant?.restaurantName || "",
    restaurantShortCode: restaurant?.restaurantShortCode || restaurant?.shortCode || "",
  };
}

const EatsAdminCreateRestaurantPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  }, []);
  const currentUserId = currentUser?.userId || null;

  const [tab, setTab] = useState("company");
  const [activeRestaurant, setActiveRestaurant] = useState(null);

  useEffect(() => {
    const requestedId = searchParams.get("restaurantId");
    if (!requestedId) {
      return undefined;
    }

    let cancelled = false;
    const loadRestaurant = async () => {
      try {
        const restaurant = await fetchRestaurantById(requestedId);
        if (cancelled) return;
        const next = toActiveRestaurant(restaurant, requestedId);
        if (!next) {
          setActiveRestaurant(null);
          setTab("company");
          return;
        }
        setActiveRestaurant(next);
        setTab("root-admin");
      } catch (error) {
        console.error("Failed loading restaurant:", error?.response?.data || error?.message);
        if (!cancelled) {
          setActiveRestaurant(null);
          setTab("company");
        }
      }
    };
    loadRestaurant();
    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  const handleCompanyCreated = (result) => {
    const restaurant = result?.restaurant || result || {};
    const next = toActiveRestaurant(restaurant);
    if (!next) {
      alert("Business created but restaurantId was not returned. Please retry.");
      return;
    }
    setActiveRestaurant(next);
    setTab("root-admin");
    navigate(`/eats/admin/restaurants/create?restaurantId=${next.restaurantId}`, { replace: true });
  };

  return (
    <EatsAdminLayout activeTab="restaurants">
      <div className="eats-admin-create-page">
        <EatsAdminPageHeader
          title="Create Restaurant"
          subtitle="Create restaurant and choose services, then assign root admin user."
          backTo="/eats/admin?tab=restaurants"
        />

        <EatsAdminCreateStepper
          activeStepId={tab}
          onStepChange={(next) => {
            if (next === "root-admin" && !activeRestaurant?.restaurantId) return;
            setTab(next);
          }}
          steps={[
            { id: "company", label: "Business" },
            {
              id: "root-admin",
              label: "Create Root Admin",
              disabled: !activeRestaurant?.restaurantId,
            },
          ]}
        />

        <div className="eats-admin-panel">
          {tab === "company" ? (
            <AddRestaurant
              embedded
              createdById={currentUserId}
              onSuccess={handleCompanyCreated}
              onClose={() => {}}
            />
          ) : activeRestaurant ? (
            <RootAdminAssignPanel
              restaurantId={activeRestaurant.restaurantId}
              restaurantName={activeRestaurant.restaurantName}
              createdById={currentUserId}
              title={`Create Owner for ${
                activeRestaurant.restaurantName || `Restaurant ${activeRestaurant.restaurantId}`
              }`}
              onSuccess={() => navigate("/eats/admin?tab=restaurants")}
            />
          ) : (
            <div className="eats-admin-empty-state">Please create a restaurant first.</div>
          )}
        </div>
      </div>
    </EatsAdminLayout>
  );
};

export default EatsAdminCreateRestaurantPage;
